import { scanFile } from "@openhealthnz-credentials/pdf-image-qr-scanner";
import { decode, decodeBinary, generateQRData } from "@mosip/pixelpass";
import {
  HEADER_DELIMITER,
  SUPPORTED_QR_HEADERS,
  CLAIM169_QR_PREFIX,
} from "./config";
import * as pdfjsLib from "pdfjs-dist/webpack";
import { isWasmQrScannerEnabled } from "../configs/featureFlags";

const zxing = await window.ZXing();

const readQRcodeFromImageFile = async (file, format, isPDF) => {
  const arrayBuffer = await file.arrayBuffer();
  const u8Buffer = new Uint8Array(arrayBuffer);
  const zxingBuffer = zxing._malloc(u8Buffer.length);
  zxing.HEAPU8.set(u8Buffer, zxingBuffer);
  const results = zxing.readBarcodesFromImage(
    zxingBuffer,
    u8Buffer.length,
    true,
    format,
    0xff
  );

  zxing._free(zxingBuffer);

  if (results.size() === 0) {
    if (!isPDF) {
      throw new Error(`No ${format} found`);
    }
  } else {
    for (let i = 0; i < results.size(); i += 1) {
      const { bytes } = results.get(i);
      return bytes;
    }
  }
};

const readQRcodeFromPdf = async (file, format) => {
  const pdfData = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
  let result;
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 2.0 });
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };
    await page.render(renderContext).promise;
    const dataURL = canvas.toDataURL();
    const blob = await (await fetch(dataURL)).blob();
    const qrCode = await readQRcodeFromImageFile(blob, format, true);
    if (qrCode) {
      result = qrCode;
    }
  }
  if (result) {
    return result;
  } else {
    throw new Error(`No ${format} found`);
  }
};

const payloadDedupeKey = (payload) => {
  if (typeof payload === "string") return payload;
  if (payload instanceof Uint8Array) {
    try {
      return new TextDecoder("utf-8").decode(payload);
    } catch {
      return String(payload);
    }
  }
  return String(payload);
};

export const dedupeQrPayloads = (payloads) => {
  const seen = new Set();
  const unique = [];
  for (const payload of payloads || []) {
    if (payload == null || payload === "") continue;
    const key = payloadDedupeKey(payload);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(payload);
  }
  return unique;
};

const scanFilesForQrLegacy = async (selectedFile) => {
  let scanResult = { data: null, error: null };
  const format = "QRCode";

  try {
    const fileType = selectedFile.type;
    let qrResult;

    if (fileType === "application/pdf") {
      qrResult = await readQRcodeFromPdf(selectedFile, format);
    } else {
      try {
        qrResult = await readQRcodeFromImageFile(selectedFile, format);
      } catch (e) {
        const arrayBuffer = await selectedFile.arrayBuffer();
        const u8Buffer = new Uint8Array(arrayBuffer);
        const zxingBuffer = zxing._malloc(u8Buffer.length);
        zxing.HEAPU8.set(u8Buffer, zxingBuffer);

        const thresholds = [0xff, 0xcc, 0x99, 0x66];
        for (const threshold of thresholds) {
          const results = zxing.readBarcodesFromImage(
            zxingBuffer,
            u8Buffer.length,
            true,
            format,
            threshold
          );

          if (results.size() > 0) {
            qrResult = results.get(0).bytes;
            break;
          }
        }

        zxing._free(zxingBuffer);

        if (!qrResult) {
          throw new Error(`No ${format} found after multiple attempts`);
        }
      }
    }

    scanResult.data = qrResult;
  } catch (e) {
    console.error("Scanning error:", e);
    scanResult.error = e.message || "Failed to scan QR code";
  }

  return scanResult;
};

/**
 * Multi-QR scan. When WASM is enabled, uses the zxing-wasm pipeline; otherwise
 * wraps the legacy single-QR path so callers (public portal) still work.
 */
export const scanFilesForAllQr = async (selectedFile) => {
  if (isWasmQrScannerEnabled()) {
    const { scanFilesForAllQrWasm } = await import("./qr-utils-wasm");
    return scanFilesForAllQrWasm(selectedFile);
  }

  const single = await scanFilesForQrLegacy(selectedFile);
  if (single?.data == null) {
    return { data: [], error: single?.error || null };
  }
  const payloads = Array.isArray(single.data) ? single.data : [single.data];
  return { data: dedupeQrPayloads(payloads), error: single.error || null };
};

export const scanFilesForQr = async (selectedFile) => {
  if (isWasmQrScannerEnabled()) {
    const all = await scanFilesForAllQr(selectedFile);
    return {
      data: all.data.length > 0 ? all.data[0] : null,
      error: all.error,
    };
  }
  return scanFilesForQrLegacy(selectedFile);
};

const ZIP_HEADER = "PK";

export const decodeQrData = async (qrData) => {
  if (!!!qrData) return;
  // Handle case where qrData is an object with data property
  const dataToDecode =
    typeof qrData === "object" && qrData !== null ? qrData.data : qrData;

  let encodedData = dataToDecode;
  if (!!HEADER_DELIMITER) {
    const splitQrData = dataToDecode.split(HEADER_DELIMITER);
    const header = splitQrData[0];
    if (SUPPORTED_QR_HEADERS.indexOf(header) === -1) return; // throw some error and handle it
    if (splitQrData.length !== 2) return; // throw some error and handle it
    encodedData = splitQrData[1];
  }

  // If the data is already a string, no need to decode it
  if (typeof encodedData === "string") {
    if (encodedData.startsWith("https:")) {
      return encodedData;
    }
    if (encodedData.startsWith(CLAIM169_QR_PREFIX)) {
      return encodedData;
    }
    if (encodedData.startsWith(ZIP_HEADER)) {
      // Convert string to Uint8Array for decodeBinary
      const encoder = new TextEncoder();
      const binaryData = encoder.encode(encodedData);
      return await decodeBinary(binaryData);
    }
    return decode(encodedData);
  }

  // Only try to decode if it's binary data
  try {
    let decodedData = new TextDecoder("utf-8").decode(encodedData);
    if (decodedData.startsWith(ZIP_HEADER)) {
      return await decodeBinary(encodedData);
    }
    if (decodedData.startsWith("https:")) {
      return decodedData;
    }
    if (decodedData.startsWith(CLAIM169_QR_PREFIX)) {
      return decodedData;
    }
    return decode(decodedData);
  } catch (error) {
    console.error("Decoding error:", error);
    return null;
  }
};

export const scanFilesWithURLForQr = async (selectedFile) => {
  let scanResult = { data: null, error: null };
  try {
    scanResult.data = await scanFile(selectedFile);
  } catch (e) {
    // Example Error Handling
    if (e?.name === "InvalidPDFException") {
      scanResult.error = "Invalid PDF";
    } else if (e instanceof Event) {
      scanResult.error = "Invalid Image";
    } else {
      scanResult.error = "Unknown error:" + e;
    }
  }
  return scanResult;
};

export const encodeData = (data) => generateQRData(data);
